import React, { useState, useEffect } from "react";
import Button from "./Button";
import noImage from "../data/images/no-image4.jpeg";
import { FaSpinner } from "react-icons/fa";
import { useStateContext } from "../contexts/ContextProvider";
import { apiAddTenantSignature, apiUploadUserId } from "../services/apiService";
import ProgressBar from "./ProgressBar";
import { HiOutlineIdentification } from "react-icons/hi2";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { storage } from "../services/firebaseConfig";
import { getRandomString } from "../helpers/randomString";
import ActiveLeaseCard from "./ActiveLeaseCard";
import { DateTime } from "luxon";

const initialErrorState = {
  fileError: "",
  signatureName: "",
  signatureTerms: "",
};

const initialSignature = {
  font: "font-signature-1",
  terms: false,
};

const TenantSignLeaseModal = (props) => {
  const { lease } = props;
  const { user, setUser, leases, setLeases, modal, handleModal } =
    useStateContext();
  const [editedLease, setEditedLease] = useState(lease);
  const [identification, setIdentification] = useState("");
  const [signature, setSignature] = useState(initialSignature);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(initialErrorState);
  const [fileSizeError, setFileSizeError] = useState(false);
  const [fileTypeError, setFileTypeError] = useState(false);
  const [page, setPage] = useState(1);

  const pages = 6;
  const maxFileSizeMB = 5; // max file size in Megabytes

  const nextPage = async () => {
    if (hasErrors()) {
      return;
    } else {
      setPage(page + 1);
    }
  };

  const prevPage = () => {
    setPage(page - 1);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditedLease((prevState) => ({ ...prevState, [name]: value }));
  };

  const handleSignature = (e) => {
    const { name, value } = e.target;
    setSignature((prevState) => ({ ...prevState, [name]: value }));
  };

  const handleSignLease = async () => {
    if (hasErrors()) {
      return;
    } else {
      editedLease.tenantFont = signature.font;
      editedLease.tenantDateSigned = DateTime.now().toISO();
      // editedLease.status = "pending";
      const response = await apiAddTenantSignature(editedLease.id, editedLease);
      if (response.hasError) {
        //TODO: add error message
        console.log(response.error);
      } else {
        setLeases(
          leases.toSpliced(
            leases.findIndex((x) => x.id === editedLease.id),
            1,
            response.data
          )
        );

        // add a delay with spinner
        setIsLoading(true);
        setTimeout(() => {
          setIsLoading(false);
          localStorage.removeItem("inviteId");
          handleModal("tenantSignLease");
        }, 1500);
        clearTimeout();
      }
    }
  };

  const handleUpload = async (event) => {
    const file = event.target.files[0];
    setFileSizeError(false);
    setFileTypeError(false);
    if (file.size > maxFileSizeMB * 1024 * 1024) {
      setFileSizeError(true);
      document.getElementById("image").value = "";
      return;
    } else if (file.type.match("image/*") || file.type === "application/pdf") {
      // define storge location, and new file name
      const imageRef = ref(
        storage,
        `identifications/${user.uuid}/${getRandomString(20)}.${
          file.name.split(".")[1]
        }`
      );

      // upload file to cloud
      await uploadBytes(imageRef, file).then(async (snapshot) => {
        await getDownloadURL(snapshot.ref).then((url) => {
          // collect url, and add to array
          console.log("uploaded file");
          saveFile(url);
        });
      });
      setIdentification(file);
      return;
    }
    setFileTypeError(true);
    document.getElementById("image").value = "";
  };

  const saveFile = async (url) => {
    console.log("save file");
    const oldFile = user.identification;
    const response = await apiUploadUserId(user.id, { identification: url });
    if (response.hasError) {
      //TODO: ADD Error Message
      console.log(response.error);
      return;
    } else {
      setUser(response.data);
      if (oldFile) removeOldFile(oldFile);
    }
  };

  const removeOldFile = (url) => {
    console.log(url);
    // delete image from image server
    const imageRef = ref(storage, url);
    getDownloadURL(imageRef)
      .then(() => {
        // File exists, proceed with deletion
        deleteObject(imageRef)
          .then(() => {
            console.log("File deleted successfully!");
          })
          .catch((error) => {
            console.error("Error deleting file:", error);
          });
      })
      .catch((error) => {
        // File does not exist or other error occurred
        if (error.code === "storage/object-not-found") {
          console.log("File does not exist, no need to delete.");
        } else {
          console.error("Error checking file existence:", error);
        }
      });
  };

  const hasErrors = () => {
    if (
      page === 4 &&
      (!user.identification || fileSizeError || fileTypeError)
    ) {
      activateError("fileError");
      return true;
    } else if (page === 6 && signature.terms === false) {
      activateError("signatureTerms");
      return true;
    } else {
      return false;
    }
  };

  const activateError = (item) => {
    setError((prevState) => ({
      ...prevState,
      [item]: "animate-shake text-red-600 border-red-600",
    }));
    setTimeout(() => {
      setError((prevState) => ({
        ...prevState,
        [item]: "",
      }));
    }, 500);
    clearTimeout();
  };

  return (
    <div className="w-screen h-screen fixed top-0 left-0 right-0 overflow-y-scroll bg-white mt-20 z-10">
      {/* {modal.messages && ( 
        <MessagesModal
          senderId={lease.tenantId}
          recipientId={lease.landlordId}
        />
      )} */}
      <ProgressBar pages={pages} currentPage={page} />
      {/* seperate into a component */}
      {isLoading && (
        <>
          <div className="fixed top-0 bottom-0 left-0 right-0 flex justify-center bg-black opacity-45"></div>
          <div className="fixed top-1/3 flex justify-center w-full text-5xl">
            <FaSpinner className="text-snappily-green animate-spin" />
          </div>
        </>
      )}
      {page === 1 && (
        <div className="grid justify-items-center mt-20 mb-[186px]">
          <div className="h-28 w-28 rounded-full bg-gray-300">
            <img
              src={
                lease.landlordAvatar instanceof Blob
                  ? URL.createObjectURL(lease.LandlordAvatar)
                  : lease.landlordAvatar || noImage
                //remove this logic once image handling is setup
              }
              alt={lease.landlordFirstName + " " + lease.landlordLastName}
              className="h-28 w-28 object-cover rounded-full drop-shadow-xl"
            />
          </div>
          <div className="text-xl mt-5">
            {lease.landlordFirstName + " " + lease.landlordLastName}
          </div>
          <div>Has invited you to sign a lease</div>
        </div>
      )}
      {page === 2 && (
        <div className="grid justify-items-center mt-10 mb-[278px]">
          <div>Ensure your legal name is correct:</div>
          <form className="grid mt-5 gap-y-2 pr-5">
            <label className="grid grid-cols-3 relative">
              First Name:
              <input
                id="tenant-first-name"
                name="tenantFirstName"
                type="text"
                value={editedLease.tenantFirstName}
                autoComplete="true"
                className="absolute left-24 col-span-2 border-gray-300 border-b-1
                 outline-snappily-orange pl-1"
                onChange={handleChange}
              />
            </label>
            <label className="grid grid-cols-3 relative">
              Middle:
              <input
                id="tenant-middle-name"
                name="tenantMiddleName"
                type="text"
                value={editedLease.tenantMiddleName || ""}
                autoComplete="true"
                className="absolute left-24 col-span-2 border-gray-300 border-b-1
                 outline-snappily-orange pl-1"
                onChange={handleChange}
              />
            </label>
            <label className="grid grid-cols-3 relative">
              Last Name:
              <input
                id="tenant-last-name"
                name="tenantLastName"
                type="text"
                value={editedLease.tenantLastName}
                autoComplete="true"
                className="absolute left-24 col-span-2 border-gray-300 border-b-1
                 outline-snappily-orange pl-1"
                onChange={handleChange}
              />
            </label>
          </form>
        </div>
      )}
      {page === 3 && (
        <div className="flex justify-center mt-10 px-10 mb-[156px]">
          <div className="border-gray-500 border-2 rounded-3xl bg-gray-300 p-5">
            <p className="text-center">Please upload a government ID.</p>
            <ul className="list-disc mt-5 pl-10">
              <li>Driver's License</li>
              <li>State ID</li>
              <li>Passport</li>
              <li>Passport Card</li>
              <li>Green Card</li>
            </ul>
            <p className="text-center mt-5">Click "Next" to proceed.</p>
          </div>
        </div>
      )}
      {page === 4 && (
        <div className="grid justify-items-center mt-5">
          <div
            className={`flex justify-center text-2xl mt-10 ${error.fileError}`}
          >
            Upload the front of your ID
          </div>
          <p className="flex justify-center text-snappily-orange text-sm">
            (Name must match your legal name)
          </p>
          <div className="flex justify-center">
            {!user.identification && (
              <HiOutlineIdentification className="text-9xl" />
            )}
            {user.identification && (
              <div className="h-28 bg-gray-300 mt-4">
                <img
                  src={
                    user.identification instanceof Blob
                      ? URL.createObjectURL(user.identification)
                      : user.identification
                  }
                  alt={`${user.firstName}`}
                  className="h-28  object-cover drop-shadow-xl cursor-pointer"
                  // onClick={handleClick}
                />
              </div>
            )}
          </div>
          <div className="grid justify-items-center mt-5 mb-[150px]">
            <input
              id="image"
              name="image"
              type="file"
              accept="image/*, application/pdf"
              className={`block w-72 border-gray-300 border rounded-lg p-1 ${error.fileError}`}
              onChange={handleUpload}
            />
            {fileSizeError && (
              <div className="text-red-500">File is too large!</div>
            )}
            {fileTypeError && (
              <div className="text-red-500">Images or PDFs only!</div>
            )}
          </div>
        </div>
      )}
      {page === 5 && (
        <>
          <div className="flex justify-center text-xl mt-5">Lease Preview</div>
          <div className="flex justify-center mb-[52px]">
            <ActiveLeaseCard
              lease={editedLease}
              senderId={editedLease.tenantId}
              recipientId={editedLease.landlordId}
              tenantView={true}
            />
          </div>
        </>
      )}
      {page === 6 && (
        <div className="grid justify-items-center mt-5">
          <div className="text-2xl">Adopt Your Signature Below:</div>
          <form>
            <label className="grid justify-items-center text-lg mt-5">
              Select a Style:
              <select
                id="font"
                name="font"
                value={signature.font}
                autoFocus={true}
                className="border-gray-300 border-1 rounded-lg
                 outline-snappily-orange pl-1"
                onChange={handleSignature}
              >
                <option value="font-signature-1">Style 1</option>
                <option value="font-signature-2">Style 2</option>
                <option value="font-signature-3">Style 3</option>
                <option value="font-signature-4">Style 4</option>
                <option value="font-signature-5">Style 5</option>
              </select>
            </label>
          </form>
          <div className="h-40 text-2xl font-signature-1 pt-10 capitalize">
            <div className={`text-center ${signature.font}`}>
              {editedLease.tenantFirstName +
                (editedLease.tenantMiddleName
                  ? " " + editedLease.tenantMiddleName + " "
                  : " ") +
                editedLease.tenantLastName}
            </div>

            <div className={`text-center pt-5  ${signature.font}`}>
              {editedLease.tenantFirstName[0] +
                (editedLease.tenantMiddleName
                  ? "." + editedLease.tenantMiddleName[0] + "."
                  : ".") +
                editedLease.tenantLastName[0] +
                "."}
            </div>
          </div>
          <div className="h-40 mt-5">
            <div className="flex justify-center items-start px-8">
              <input
                id="checkbox"
                name="tou"
                type="checkbox"
                checked={signature.terms}
                onChange={() =>
                  setSignature((prevState) => ({
                    ...prevState,
                    terms: !signature.terms,
                  }))
                }
                className="mt-3 mr-3"
              />
              <div
                className={`max-w-[500px] text-justify cursor-pointer ${error.signatureTerms}`}
                onClick={() =>
                  setSignature((prevState) => ({
                    ...prevState,
                    terms: !signature.terms,
                  }))
                }
              >
                By checking this box, I agree to adopt the signature and
                initials above as an electronic representation of my signature
                and initials, for the purpose of signing this legally binding
                lease.
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="flex justify-center w-full mb-48">
        {page !== 1 && (
          <Button
            background="bg-slate-400"
            text="text-white"
            opacity="bg-opacity-100"
            title="Back"
            height="h-10"
            width="w-24"
            borderColor="border-none"
            borderHover="hover:border-snappily-orange"
            customFunc={prevPage}
          />
        )}
        <Button
          background="bg-snappily-orange"
          text="text-white"
          opacity="bg-opacity-100"
          title={page === pages ? "Sign" : page === 1 ? "Begin" : "Next"}
          height="h-10"
          width="w-24"
          borderColor="border-none"
          borderHover="hover:border-snappily-orange"
          customFunc={() => {
            page === pages ? handleSignLease() : nextPage();
            // localStorage.removeItem("inviteId");
          }}
        />
      </div>
    </div>
  );
};

export default TenantSignLeaseModal;
